// components/process-units/WasteWaterDisposal.js
import React, { useState } from 'react';

const WasteWaterDisposal = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Waste Water Disposal</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Waste water disposal treats produced water from the gas processing plant to meet
            environmental regulations before discharge or reinjection. This may include removal
            of hydrocarbons, dissolved solids, and other contaminants.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Disposal Method</label>
              <select 
                value={config.disposalMethod || 'reinjection'}
                onChange={(e) => updateConfig({ disposalMethod: e.target.value })}
              >
                <option value="reinjection">Reinjection</option>
                <option value="treatment">Treatment and Discharge</option>
                <option value="evaporation">Evaporation Pond</option>
              </select>
            </div>
            
            {config.disposalMethod === 'reinjection' && (
              <>
                <div className="parameter-row">
                  <label>Injection Pressure (bar)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    step="10"
                    value={config.injectionPressure || 150}
                    onChange={(e) => handleInputChange('injectionPressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Injection Pump Efficiency (%)</label>
                  <input
                    type="number"
                    min="40"
                    max="95"
                    step="1"
                    value={(config.pumpEfficiency || 0.75) * 100}
                    onChange={(e) => handleInputChange('pumpEfficiency', e.target.value / 100)}
                  />
                </div>
              </>
            )}
            
            {config.disposalMethod === 'treatment' && (
              <>
                <div className="parameter-row">
                  <label>Treatment Level</label>
                  <select 
                    value={config.treatmentLevel || 'primary'}
                    onChange={(e) => updateConfig({ treatmentLevel: e.target.value })}
                  >
                    <option value="primary">Primary (Oil/Water Separation)</option>
                    <option value="secondary">Secondary (Dissolved Air Flotation)</option>
                    <option value="tertiary">Tertiary (Filtration/Biological)</option>
                    <option value="advanced">Advanced (RO/Ion Exchange)</option>
                  </select>
                </div>
                
                <div className="parameter-row">
                  <label>Aeration Power (kW)</label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="10"
                    value={config.aerationPower || 50}
                    onChange={(e) => handleInputChange('aerationPower', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>Water Flow Rate (m³/h)</label>
              <input
                type="number"
                min="0"
                max="1000"
                step="5"
                value={config.waterFlowRate || 20}
                onChange={(e) => handleInputChange('waterFlowRate', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Dissolved Solids (ppm)</label>
              <input
                type="number"
                min="0"
                max="200000"
                step="1000"
                value={config.dissolvedSolids || 35000}
                onChange={(e) => handleInputChange('dissolvedSolids', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Chemical Treatment</label>
              <select 
                value={config.chemicalTreatment ? 'yes' : 'no'}
                onChange={(e) => updateConfig({ chemicalTreatment: e.target.value === 'yes' })}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>Reinjection typically requires significant pumping energy but minimal treatment.</li>
              <li>Treatment for discharge requires energy for aeration, mixing, and pumping.</li>
              <li>Higher dissolved solids content increases energy requirements for treatment.</li>
              <li>Water quality targets are determined by local environmental regulations.</li>
              <li>Chemical treatment may reduce energy consumption but increases operational costs.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default WasteWaterDisposal;